import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache, makeVar } from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";
import { relayStylePagination } from "@apollo/client/utilities";
import { setContext } from '@apollo/client/link/context';
import { DateTime } from "luxon";
import { currentBusinessIdVar, currentSubscriptionBenefitVar, unsavedMembershipTierEditsVar } from "@withfriendsco/wf-components";
export { currentBusinessIdVar, currentSubscriptionBenefitVar } from "@withfriendsco/wf-components";
const uri = process.env.REACT_APP_GRAPHQL_API;
// const uri = "https://wf-graphql-gateway.herokuapp.com/graphql"
const httpLink = createHttpLink({ uri });
const authLink = setContext((_, { headers }) => {
    var _a;
    let token = ((_a = document.querySelector('meta[name="withfriends:t"]')) === null || _a === void 0 ? void 0 : _a.getAttribute('content')) || null;
    return {
        headers: Object.assign(Object.assign({}, headers), { authorization: token ? `Bearer ${token}` : "" })
    };
});
const retryLink = new RetryLink({
    delay: {
        initial: 500,
        max: Infinity,
        jitter: true
    },
    attempts: {
        max: 10,
        retryIf: (error, _operation) => {
            const willRetry = !!error && !error.message.includes('400');
            if (willRetry)
                console.log("retrying", error.message);
            return willRetry;
        }
    }
});
const gatewayRetryLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((data) => {
        var _a;
        if ((_a = data === null || data === void 0 ? void 0 : data.errors) === null || _a === void 0 ? void 0 : _a.find(({ message }) => message.includes("request to") && message.includes("failed") && message.includes("reason"))) {
            throw new Error('Gateway internal error');
        }
        return data;
    });
});
const cleanTypeName = new ApolloLink((operation, forward) => {
    if (operation.variables) {
        const omitTypename = (key, value) => (key === '__typename' ? undefined : value);
        operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
    }
    return forward(operation).map((data) => {
        return data;
    });
});
const linkMe = ApolloLink.from([
    cleanTypeName,
    authLink,
    retryLink,
    gatewayRetryLink,
    httpLink,
]);
export const fetchIntegrations = async (alias) => {
    var _a;
    let token = ((_a = document.querySelector('meta[name="withfriends:t"]')) === null || _a === void 0 ? void 0 : _a.getAttribute('content')) || null;
    const response = await fetch(`${process.env.REACT_APP_RAILS_API}/merchants/${alias}.json`, {
        method: 'GET',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
        })
    });
    try {
        const body = await response.json();
        return body;
    }
    catch (err) {
        console.log(err);
    }
};
export const primaryActionLoadingVar = makeVar(false);
export const fulfillmentScreenQueryVar = makeVar({
    businessId: currentBusinessIdVar(),
    isFulfilled: false,
    end: DateTime.local().toSQLDate(),
});
export const fulfillmentScreenFirstVar = makeVar(20);
export const fulfillmentScreenAfterVar = makeVar(null);
export const TestError = () => {
    throw new Error('test error');
};
const cache = new InMemoryCache({
    typePolicies: {
        Query: {
            fields: {
                fulfillments: relayStylePagination(["query"]),
                subscriptionBenefit: {
                    read: (_, { args, toReference }) => {
                        return toReference({
                            __typename: "SubscriptionBenefit",
                            id: args.id,
                        });
                    }
                },
                currentBusinessId: {
                    read: () => {
                        return currentBusinessIdVar();
                    },
                },
                customerDataComplete: {
                    read: async (alias) => {
                        var _a;
                        const response = await fetchIntegrations(alias);
                        return (_a = response === null || response === void 0 ? void 0 : response.customer_data) === null || _a === void 0 ? void 0 : _a.complete;
                    },
                },
                currentSubscriptionBenefit: {
                    read: () => {
                        return currentSubscriptionBenefitVar();
                    },
                },
            }
        },
        MembershipTier: {
            merge: true,
            fields: {
                name: {
                    read(existing, { readField }) {
                        const id = readField('id');
                        const overlay = unsavedMembershipTierEditsVar()[id];
                        return (overlay && overlay.name !== undefined) ? overlay.name : existing;
                    }
                },
                benefits: {
                    read(existing, { readField }) {
                        const id = readField('id');
                        const overlay = unsavedMembershipTierEditsVar()[id];
                        return (overlay && overlay.benefits !== undefined) ? overlay.benefits : existing;
                    }
                },
                plans: {
                    read(existing, { readField }) {
                        const id = readField('id');
                        const overlay = unsavedMembershipTierEditsVar()[id];
                        return (overlay && overlay.plans !== undefined) ? overlay.plans : existing;
                    }
                },
                discounts: {
                    read(existing, { readField }) {
                        const id = readField('id');
                        const overlay = unsavedMembershipTierEditsVar()[id];
                        return (overlay && overlay.discounts !== undefined) ? overlay.discounts : existing;
                    }
                },
                earlyAccess: {
                    read(existing, { readField }) {
                        const id = readField('id');
                        const overlay = unsavedMembershipTierEditsVar()[id];
                        return (overlay && overlay.earlyAccess !== undefined) ? overlay.earlyAccess : existing;
                    }
                }
            }
        },
        SubscriptionBenefit: {
            merge: true,
        },
        WelcomeGiftBenefit: {
            merge: true,
        },
        Address: {
            merge: true,
        },
        Fulfillment: {
            merge: true,
            fields: {
                address: {
                    merge: (existing = {}, incoming, { mergeObjects }) => {
                        return mergeObjects(existing, incoming);
                    }
                }
            }
        }
    },
    possibleTypes: {
        Benefit: ["RegularBenefit", "SubscriptionBenefit", "WelcomeGiftBenefit", "RedemptionBenefit"]
    },
});
const client = new ApolloClient({
    link: linkMe,
    cache: cache,
    name: 'React Front-End',
    version: '1.0',
    connectToDevTools: true,
});
export default client;
